/**
 * The CTA's decorative pixel wave: ONE sequential pulse per pointer entry or
 * keyboard-focus arrival, then back to rest. The stylesheet animates the
 * three cells while the button carries `data-wave="1"`; this hook sets that
 * attribute for the wave's duration and never while a wave is already
 * running, and does nothing at all when less motion was asked for.
 * Spinners are the only pixels on the page allowed to loop.
 *
 * A focus that follows a pointer press on the button is the click's, not
 * the keyboard's: the pointer entry already ran the wave, so that focus
 * runs none.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { reduceMotionRequested } from './motion.js';

/** The wave's three cells at 400ms plus the last cell's 120ms delay, with a little rest. */
export const PIXEL_WAVE_MS = 560;

export interface PixelWave {
  readonly waving: boolean;
  readonly onMouseEnter: () => void;
  readonly onPointerDown: () => void;
  readonly onFocus: () => void;
}

export function usePixelWave(): PixelWave {
  const [waving, setWaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const active = useRef(false);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const wave = useCallback(() => {
    if (reduceMotionRequested() || active.current) return;
    active.current = true;
    setWaving(true);
    timer.current = setTimeout(() => {
      active.current = false;
      timer.current = null;
      setWaving(false);
    }, PIXEL_WAVE_MS);
  }, []);

  const viaPointer = useRef(false);
  const onPointerDown = useCallback(() => {
    viaPointer.current = true;
  }, []);
  const onFocus = useCallback(() => {
    const keyboard = !viaPointer.current;
    viaPointer.current = false;
    if (keyboard) wave();
  }, [wave]);

  return { waving, onMouseEnter: wave, onPointerDown, onFocus };
}
